import * as THREE from "three";
import Experience from "../Experience";
import fragmentShader from "../shaders/backgrounds/space/fragment.glsl?raw";
import vertexShader from "../shaders/backgrounds/space/vertex.glsl?raw";

interface Comet {
  line: THREE.Line;
  velocity: THREE.Vector3;
  lifetime: number;
  age: number;
  headPosition: THREE.Vector3;
}

export default class SpaceBackground {
  public particles: THREE.Points<THREE.BufferGeometry, THREE.ShaderMaterial>;

  private experience: Experience;
  private geometry: THREE.BufferGeometry;
  private material: THREE.ShaderMaterial;
  private particleCount = 100000;
  private backgroundColor = 0x000000;
  private colorPalette = ["#dcdff9", "#ce9219", "#b9ad0c"];
  private comets: Comet[] = [];
  private cometSpawnTimer = 0;
  private cometSpawnInterval = 1.2; // seconds
  private cometMinSpeed = 1100;
  private cometMaxSpeed = 2650;
  private cometMaxLifetime = 1;
  private starMinZ = -800; // How far back stars spawn
  private starMaxZ = 800; // How close stars can be

  constructor() {
    this.experience = new Experience();

    // Set scene background to black
    this.experience.scene.background = new THREE.Color(this.backgroundColor);

    // Create geometry for particles
    this.geometry = new THREE.BufferGeometry();

    // Create shader material for per-particle sizes
    this.material = new THREE.ShaderMaterial({
      uniforms: {
        uOpacity: { value: 1.0 },
        uMinZ: { value: this.starMinZ },
        uMaxZ: { value: this.starMaxZ },
      },
      vertexShader,
      fragmentShader,
      transparent: true,
      depthWrite: false,
    });

    this.particles = new THREE.Points(this.geometry, this.material);
    this.particles.renderOrder = -1;

    this.experience.scene.add(this.particles);

    // Generate initial particles
    this.generateParticles();

    // Add debug controls
    this.setupDebug();

    // Start hidden
    this.hide();
  }

  private generateParticles() {
    const positions = new Float32Array(this.particleCount * 3);
    const colors = new Float32Array(this.particleCount * 3);
    const sizes = new Float32Array(this.particleCount);
    const randomness = new Float32Array(this.particleCount);

    // Same size as PaperBackground: 4096x4096
    const width = 3124;
    const height = 3124;
    const minZ = this.starMinZ; // Far back
    const maxZ = this.starMaxZ; // Closest

    for (let i = 0; i < this.particleCount; i++) {
      const i3 = i * 3;

      // Random position within the background size
      positions[i3] = (Math.random() - 0.5) * width;
      positions[i3 + 1] = (Math.random() - 0.5) * height;

      // Random Z position (deep in space)
      const z = minZ + Math.random() * (maxZ - minZ);
      positions[i3 + 2] = z;

      // Size based on Z depth (closer = bigger)
      // Normalize z to 0-1 range (0 = far, 1 = close)
      const depthFactor = (z - minZ) / (maxZ - minZ);
      sizes[i] = 2 + depthFactor * 8; // Size range: 2-10

      // Random brightness variation per star (0.3 to 1.0)
      randomness[i] = 0.3 + Math.random() * 0.7;

      // Random color from palette
      const color = new THREE.Color(
        this.colorPalette[Math.floor(Math.random() * this.colorPalette.length)]
      );
      colors[i3] = color.r;
      colors[i3 + 1] = color.g;
      colors[i3 + 2] = color.b;
    }

    this.geometry.setAttribute(
      "position",
      new THREE.BufferAttribute(positions, 3)
    );
    this.geometry.setAttribute("color", new THREE.BufferAttribute(colors, 3));
    this.geometry.setAttribute("size", new THREE.BufferAttribute(sizes, 1));
  }

  private setupDebug() {
    if (this.experience.debug.ui) {
      const folder = this.experience.debug.ui.addFolder("Space Background");

      const params = {
        particleCount: this.particleCount,
        backgroundColor: this.backgroundColor,
        cometSpawnInterval: this.cometSpawnInterval,
        cometMinSpeed: this.cometMinSpeed,
        cometMaxSpeed: this.cometMaxSpeed,
        cometMaxLifetime: this.cometMaxLifetime,
        starMinZ: this.starMinZ,
        starMaxZ: this.starMaxZ,
      };

      folder
        .add(params, "particleCount")
        .min(100)
        .max(1000000)
        .step(100)
        .name("Star Count")
        .onChange((value: number) => {
          this.particleCount = value;
          this.regenerateParticles();
        });

      folder
        .addColor(params, "backgroundColor")
        .name("Background Color")
        .onChange((value: number) => {
          this.backgroundColor = value;
          this.experience.scene.background = new THREE.Color(value);
        });

      folder
        .add(this.material.uniforms.uOpacity, "value")
        .min(0)
        .max(1)
        .step(0.1)
        .name("Opacity");

      folder
        .add(params, "starMinZ")
        .min(-5000)
        .max(-50)
        .step(10)
        .name("Star Min Z (far)")
        .onChange((value: number) => {
          this.starMinZ = value;
          this.material.uniforms.uMinZ.value = value;
          this.regenerateParticles();
        });

      folder
        .add(params, "starMaxZ")
        .min(-500)
        .max(-10)
        .step(5)
        .name("Star Max Z (close)")
        .onChange((value: number) => {
          this.starMaxZ = value;
          this.material.uniforms.uMaxZ.value = value;
          this.regenerateParticles();
        });

      const cometFolder = folder.addFolder("Comets");

      cometFolder
        .add(params, "cometSpawnInterval")
        .min(0.1)
        .max(10)
        .step(0.1)
        .name("Spawn Interval (s)")
        .onChange((value: number) => {
          this.cometSpawnInterval = value;
        });

      cometFolder
        .add(params, "cometMinSpeed")
        .min(100)
        .max(5000)
        .step(50)
        .name("Min Speed")
        .onChange((value: number) => {
          this.cometMinSpeed = value;
        });

      cometFolder
        .add(params, "cometMaxSpeed")
        .min(100)
        .max(5000)
        .step(50)
        .name("Max Speed")
        .onChange((value: number) => {
          this.cometMaxSpeed = value;
        });

      cometFolder
        .add(params, "cometMaxLifetime")
        .min(0.1)
        .max(5)
        .step(0.1)
        .name("Max Lifetime (s)")
        .onChange((value: number) => {
          this.cometMaxLifetime = value;
        });
    }
  }

  private regenerateParticles() {
    // Dispose old geometry
    this.geometry.dispose();

    // Create new geometry
    this.geometry = new THREE.BufferGeometry();
    this.particles.geometry = this.geometry;

    // Generate new particles
    this.generateParticles();
  }

  show() {
    this.particles.visible = true;
  }

  hide() {
    this.particles.visible = false;
  }

  setOpacity(value: number) {
    const clamped = THREE.MathUtils.clamp(value, 0, 1);
    this.material.uniforms.uOpacity.value = clamped;
    this.particles.visible = clamped > 0.001;
  }

  private spawnComet() {
    // Random spawn position somewhere in view
    const width = 4096;
    const height = 4096;

    const startPosition = new THREE.Vector3(
      (Math.random() - 0.5) * width * 0.8,
      (Math.random() - 0.5) * height * 0.8,
      -200 - Math.random() * 800
    );

    // Random velocity (moving across screen)
    const angle = Math.random() * Math.PI * 2;
    const speed =
      this.cometMinSpeed +
      Math.random() * (this.cometMaxSpeed - this.cometMinSpeed);
    const velocity = new THREE.Vector3(
      Math.cos(angle) * speed,
      Math.sin(angle) * speed,
      Math.random() * 100 - 50
    );

    // Create line geometry with gradient (head to tail)
    const trailLength = 50;
    const points = [
      startPosition.clone(),
      startPosition
        .clone()
        .sub(velocity.clone().normalize().multiplyScalar(trailLength)),
    ];

    const geometry = new THREE.BufferGeometry().setFromPoints(points);

    // Create colors for gradient (bright at head, transparent at tail)
    const colors = new Float32Array([
      1,
      1,
      1, // white at head
      1,
      1,
      1, // white at tail (will fade via opacity)
    ]);
    geometry.setAttribute("color", new THREE.BufferAttribute(colors, 3));

    const material = new THREE.LineBasicMaterial({
      color: 0xffffff,
      transparent: true,
      opacity: 1,
      linewidth: 2,
    });

    const line = new THREE.Line(geometry, material);

    const minLifetime = this.cometMaxLifetime * 0.3; // Min is 30% of max
    const comet: Comet = {
      line,
      velocity,
      lifetime:
        minLifetime + Math.random() * (this.cometMaxLifetime - minLifetime),
      age: 0,
      headPosition: startPosition.clone(),
    };

    this.comets.push(comet);
    this.experience.scene.add(line);
  }

  removeExpiredComets(deltaTime: number) {
    for (let i = this.comets.length - 1; i >= 0; i--) {
      const comet = this.comets[i];
      comet.age += deltaTime;

      // Update head position
      comet.headPosition.x += comet.velocity.x * deltaTime;
      comet.headPosition.y += comet.velocity.y * deltaTime;
      comet.headPosition.z += comet.velocity.z * deltaTime;

      // Update line geometry to follow head
      const trailLength = 50;
      const tailPosition = comet.headPosition
        .clone()
        .sub(comet.velocity.clone().normalize().multiplyScalar(trailLength));

      const positions = comet.line.geometry.attributes.position;
      positions.setXYZ(
        0,
        comet.headPosition.x,
        comet.headPosition.y,
        comet.headPosition.z
      );
      positions.setXYZ(1, tailPosition.x, tailPosition.y, tailPosition.z);
      positions.needsUpdate = true;

      // Fade out over lifetime (stay bright, then fade quickly at end)
      const fadeProgress = comet.age / comet.lifetime;
      let opacity = 1;

      if (fadeProgress > 0.6) {
        // Start fading after 60% of lifetime
        const fadeAmount = (fadeProgress - 0.6) / 0.4; // 0 to 1 over last 40%
        opacity = 1 - fadeAmount;
      }

      (comet.line.material as THREE.LineBasicMaterial).opacity = opacity;

      // Remove expired comets
      if (comet.age >= comet.lifetime) {
        this.experience.scene.remove(comet.line);
        comet.line.geometry.dispose();
        (comet.line.material as THREE.Material).dispose();
        this.comets.splice(i, 1);
      }
    }
  }

  update(deltaTime: number) {
    // Update comet spawn timer
    this.cometSpawnTimer += deltaTime;
    if (this.cometSpawnTimer >= this.cometSpawnInterval) {
      this.cometSpawnTimer = 0;
      this.spawnComet();
    }

    // Update existing comets
    this.removeExpiredComets(deltaTime);
  }
}
